import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../../api';

export default function CourseDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [pages, setPages] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState('pages');
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [passingScore, setPassingScore] = useState('');
  const [showAddPage, setShowAddPage] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [pageType, setPageType] = useState('CONTENT');
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = async () => {
    const [cRes, pRes, aRes] = await Promise.all([
      api(`/lms/courses/${id}`),
      api(`/lms/courses/${id}/pages`),
      api(`/lms/courses/${id}/assignments`),
    ]);
    if (cRes.ok) { const c = await cRes.json(); setCourse(c); setTitle(c.title); setDescription(c.description || ''); setPassingScore(c.passingScore != null ? String(c.passingScore) : ''); }
    if (pRes.ok) setPages(await pRes.json());
    if (aRes.ok) setAssignments(await aRes.json());
  };

  useEffect(() => { load(); }, [id]);

  const loadEmployees = () => {
    if (employees.length) return;
    api('/hcm/employees').then(r => r.json()).then(setEmployees).catch(() => {});
  };

  const handleSave = async () => {
    const res = await api(`/lms/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ title, description, status: course.status, passingScore: passingScore !== '' ? parseInt(passingScore) : null }),
    });
    if (res.ok) { setCourse(await res.json()); setEditing(false); }
  };

  const togglePublish = async () => {
    const newStatus = course.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    const res = await api(`/lms/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ title: course.title, description: course.description, status: newStatus }),
    });
    if (res.ok) setCourse(await res.json());
  };

  const handleAddPage = async (e) => {
    e.preventDefault();
    if (!pageTitle.trim()) return;
    const res = await api(`/lms/courses/${id}/pages`, {
      method: 'POST',
      body: JSON.stringify({ title: pageTitle.trim(), pageType }),
    });
    if (res.ok) { setShowAddPage(false); setPageTitle(''); load(); }
  };

  const handleDeletePage = async (pageId) => {
    await api(`/lms/courses/${id}/pages/${pageId}`, { method: 'DELETE' });
    setDeleteConfirm(null);
    load();
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignEmployeeId) return;
    await api(`/lms/courses/${id}/assignments`, {
      method: 'POST',
      body: JSON.stringify({ employeeId: assignEmployeeId }),
    });
    setShowAssign(false);
    setAssignEmployeeId('');
    load();
  };

  const handleUnassign = async (assignmentId) => {
    await api(`/lms/courses/${id}/assignments/${assignmentId}`, { method: 'DELETE' });
    load();
  };

  const handleDeleteCourse = async () => {
    await api(`/lms/courses/${id}`, { method: 'DELETE' });
    navigate('/lms');
  };

  const handleVideoUpload = async (pageId, file) => {
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    const formData = new FormData();
    formData.append('file', file);
    await fetch(`${API_BASE}/lms/courses/${id}/pages/${pageId}/video-upload`, { method: 'POST', headers, body: formData });
    load();
  };

  const movePage = async (index, direction) => {
    const newPages = [...pages];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newPages.length) return;
    [newPages[index], newPages[swapIndex]] = [newPages[swapIndex], newPages[index]];
    setPages(newPages);
    await api(`/lms/courses/${id}/pages/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ pageIds: newPages.map(p => p.id) }),
    });
  };

  if (!course) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/lms" className="text-violet-600 hover:text-violet-800 text-sm">← Back to Courses</Link>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        {editing ? (
          <div className="space-y-4">
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none text-xl font-bold" />
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
              placeholder="Course description..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none" />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Passing Score (%)</label>
              <input type="number" min="0" max="100" value={passingScore} onChange={e => setPassingScore(e.target.value)}
                placeholder="Leave blank for no minimum"
                className="w-48 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleSave} className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700">Save</button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{course.title}</h2>
              {course.description && <p className="text-gray-600 mt-1">{course.description}</p>}
              <div className="flex items-center gap-3 mt-3">
                <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${course.status === 'PUBLISHED' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {course.status}
                </span>
                <span className="text-sm text-gray-500">{course.pageCount} pages · {course.assignmentCount} assigned{course.passingScore != null ? ` · ${course.passingScore}% to pass` : ''}{course.version > 0 ? ` · v${course.version}` : ''}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(true)} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Edit</button>
              <button onClick={togglePublish} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
                {course.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
              </button>
              <button onClick={handleDeleteCourse} className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm">Delete</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-4 border-b">
        {['pages', 'assignments'].map(t => (
          <button key={t} onClick={() => { setTab(t); if (t === 'assignments') loadEmployees(); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t === 'pages' ? `Pages (${pages.length})` : `Assignments (${assignments.length})`}
          </button>
        ))}
      </div>

      {tab === 'pages' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Course Pages</h3>
            <button onClick={() => setShowAddPage(true)} className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm">Add Page</button>
          </div>
          <div className="divide-y">
            {pages.map((page, i) => (
              <div key={page.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm w-6 text-center">{i + 1}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${page.pageType === 'VIDEO' ? 'bg-red-50 text-red-600' : page.pageType === 'QUIZ' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
                    {page.pageType}
                  </span>
                  <Link to={`/lms/courses/${id}/pages/${page.id}`} className="text-violet-600 hover:text-violet-800 font-medium">{page.title}</Link>
                  {page.pageType === 'VIDEO' && !page.videoS3Key && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">No video</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => movePage(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">↑</button>
                  <button onClick={() => movePage(i, 1)} disabled={i === pages.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">↓</button>
                  <button onClick={() => setDeleteConfirm(page)} className="p-1 text-red-400 hover:text-red-600 ml-2">✕</button>
                </div>
              </div>
            ))}
          </div>
          {pages.length === 0 && <div className="text-center py-8 text-gray-500">No pages yet. Add your first page to get started.</div>}
        </div>
      )}

      {tab === 'assignments' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Assigned Employees</h3>
            <button onClick={() => { setShowAssign(true); loadEmployees(); }} className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm">Assign Employee</button>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Assigned</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Completed</th>
                <th className="px-6 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {assignments.map(a => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{a.employeeName || a.employeeId}</td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div className="bg-violet-600 h-2 rounded-full" style={{width: `${a.totalPageCount ? (a.completedPageCount / a.totalPageCount * 100) : 0}%`}} />
                      </div>
                      <span className="text-sm text-gray-600">{a.completedPageCount}/{a.totalPageCount}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm hidden sm:table-cell">{a.assignedAt ? new Date(a.assignedAt).toLocaleDateString() : '—'}</td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    {a.completedAt
                      ? <span className="text-green-600 text-sm">{new Date(a.completedAt).toLocaleDateString()}</span>
                      : <span className="text-gray-400 text-sm">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => handleUnassign(a.id)} className="text-red-400 hover:text-red-600">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {assignments.length === 0 && <div className="text-center py-8 text-gray-500">No employees assigned yet.</div>}
        </div>
      )}

      {showAddPage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddPage(false)}>
          <form onSubmit={handleAddPage} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Page</h3>
            <input autoFocus type="text" value={pageTitle} onChange={e => setPageTitle(e.target.value)}
              placeholder="Page title" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none mb-3" />
            <select value={pageType} onChange={e => setPageType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none">
              <option value="CONTENT">Content (Slide)</option>
              <option value="VIDEO">Video</option>
              <option value="QUIZ">Quiz</option>
            </select>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setShowAddPage(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!pageTitle.trim()} className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">Add</button>
            </div>
          </form>
        </div>
      )}

      {showAssign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAssign(false)}>
          <form onSubmit={handleAssign} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign Employee</h3>
            <select value={assignEmployeeId} onChange={e => setAssignEmployeeId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none">
              <option value="">Select employee...</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
              ))}
            </select>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setShowAssign(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!assignEmployeeId} className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">Assign</button>
            </div>
          </form>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Page</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deleteConfirm.title}"?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDeletePage(deleteConfirm.id)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
